import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { ReactNode, Ref, useRef, useState } from "react";

interface TruncatedTextProps {
  /** Text to display and truncate if needed */
  children: ReactNode;
  /** Override tooltip content. Defaults to children (full text). */
  tooltip?: ReactNode;
  /** Additional classes. Merged via cn(). Must include a width constraint (e.g. max-w-*) for truncation. */
  className?: string;
  /** Tooltip placement side */
  side?: "top" | "right" | "bottom" | "left";
  /** Element to render. Use "span" inside buttons/inline contexts where <p> is invalid. */
  as?: "p" | "span" | "h1" | "h2" | "div";
}

/**
 * Single-line truncating text with conditional tooltip.
 *
 * Shows an ellipsis when text overflows, and displays a tooltip ONLY when the text
 * is actually truncated. Tooltip content defaults to the full text (children).
 *
 * Truncation is measured on hover/focus, so it always reflects the current column
 * width without needing a ResizeObserver per cell.
 *
 * The parent or this element must have a width constraint (max-w-*, w-*) for truncation to work.
 */
export const TruncatedText = ({
  children,
  tooltip = children,
  className,
  side = "top",
  as: Component = "p",
}: TruncatedTextProps) => {
  const ref = useRef<HTMLElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const measure = () => {
    const el = ref.current;
    if (!el) return;
    // scrollWidth/clientWidth are rounded to integers, so allow 1px of slack to
    // avoid false positives from sub-pixel layout widths.
    setIsTruncated(el.scrollWidth > el.clientWidth + 1);
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Component
          ref={ref as Ref<never>}
          className={cn("truncate", className)}
          onPointerEnter={measure}
          onFocus={measure}
        >
          {children}
        </Component>
      </TooltipTrigger>
      {isTruncated && (
        <TooltipContent
          side={side}
          sideOffset={4}
          className="pointer-events-none"
        >
          {tooltip}
        </TooltipContent>
      )}
    </Tooltip>
  );
};
